import { lazy, Suspense } from "react";
import AppRouter from "./AppRouter";
import { getStripeKey } from "./Environment";
import { Elements } from "@stripe/react-stripe-js";
import { CookiesProvider } from "react-cookie";
import { Provider } from "react-redux";
import { BrowserRouter } from "react-router-dom";
import { ClickToComponent } from "@freightsimple/click-to-component/ClickToComponent";
import { PageChromeProvider } from "@freightsimple/ui-core/PageLayout/PageChromeProvider";
import { ThemeProvider } from "@freightsimple/ui-core/Theme/ThemeProvider";
import { Toaster } from "@freightsimple/ui-core/Feedback/Toaster";
import { DialogHost } from "@freightsimple/ui-core/Dialog/DialogHost";
import { DashboardInspector } from "@/App/Dev/DashboardInspector";
import { showDeveloperAffordances } from "@/Helpers/developerMode";
import { loadStripeWithRetry } from "@/Helpers/loadStripeWithRetry";
import { useAuthentication } from "@/Slices/authenticationSlice";

import { store } from "./store";
import { ErrorHandlingBoundaryView } from "@/Features/ErrorHandling/CustomComponents/ErrorHandlingBoundaryView";
import AuthenticationAttributionProcessor from "@/Features/Authentication/CustomComponents/AuthenticationAttributionProcessor";

// Lazy chunk — Cmd+K palette + cold-start overlay. Only fetched for
// signed-in FreightSimple employees, so customers never pay the
// `@freightsimple/dev-tools` bundle cost.
const EmployeeDevOverlays = lazy(() => import("@/App/Dev/EmployeeDevOverlays"));

// Make sure to call `loadStripe` outside of a component’s render to avoid
// recreating the `Stripe` object on every render.
const stripePromise = loadStripeWithRetry(getStripeKey());

// expose store when run in Cypress
// oxlint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
if (window.Cypress) {
  // oxlint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  window.store = store;
}

/**
 * Mounts the Cmd+K palette + cold-start overlay only for FreightSimple
 * employees, via a lazy chunk so customers never download the
 * `@freightsimple/dev-tools` bundle. Lives inside <Provider> so it can
 * read auth state.
 */
function GatedEmployeeDevOverlays_() {
  const { user } = useAuthentication();
  if (!showDeveloperAffordances(user?.email)) return <></>;
  return (
    <Suspense fallback={<></>}>
      <EmployeeDevOverlays />
    </Suspense>
  );
}

export function App() {
  // No forceTheme: dashboard follows the stored preference, defaulting to the
  // OS setting ("system"). The theme switcher lives in the user menu.
  return (
    <ThemeProvider
      storageKey="dashboard:theme"
      // Paints the root canvas so overscroll and any gap below short pages
      // match the theme — nothing else sets a background on <html>.
      backgrounds={{ light: "#ffffff", dark: "#0c0c10" }}
    >
      <PageChromeProvider
        appName="FreightSimple"
        density="comfortable"
        gutter="host"
      >
        <Provider store={store}>
          <Elements stripe={stripePromise}>
            <CookiesProvider>
              <ClickToComponent />
              <BrowserRouter>
                <ErrorHandlingBoundaryView>
                  {/* Inside the store/router providers on purpose: when a
                      parent unmounts a still-open Dialog, the host re-renders
                      that dialog in its own tree position to play the exit
                      animation, so it needs the same context the dialog had. */}
                  <DialogHost>
                    <DashboardInspector>
                      <AppRouter />
                    </DashboardInspector>
                    <GatedEmployeeDevOverlays_ />
                    <AuthenticationAttributionProcessor />
                    {/* Every `toast.*` call needs a host to render into, or
                        it goes nowhere. Storybook mounts its own in
                        .storybook/preview.tsx. */}
                    <Toaster />
                  </DialogHost>
                </ErrorHandlingBoundaryView>
              </BrowserRouter>
            </CookiesProvider>
          </Elements>
        </Provider>
      </PageChromeProvider>
    </ThemeProvider>
  );
}
